function Technology({ onLearnMoreClick }) {
    try {
        return (
            <section data-name="technology" id="technology" className="py-24 bg-gray-900 relative overflow-hidden">
                {/* Background elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-full h-full opacity-10">
                        <div className="absolute w-96 h-96 rounded-full bg-green-500/30 blur-3xl top-20 right-20"></div>
                        <div className="absolute w-64 h-64 rounded-full bg-blue-500/20 blur-3xl bottom-20 left-20"></div>
                    </div>
                    <div className="circuit-grid"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-5xl font-bold mb-8 text-white">
                                Our <span className="text-gradient bg-gradient-to-r from-green-400 to-emerald-600">Innovative</span> Model
                            </h2>
                            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
                                Revolutionizing sustainable computing through cutting-edge technology and environmental responsibility.
                            </p>

                            {/* Key Metrics */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
                                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                                        <i className="fas fa-microchip text-green-400 text-xl"></i>
                                    </div>
                                    <div className="text-3xl font-bold text-green-400 mb-2">10 PFLOPS</div>
                                    <div className="text-gray-400">Planned Power</div>
                                </div>

                                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                                        <i className="fas fa-solar-panel text-green-400 text-xl"></i>
                                    </div>
                                    <div className="text-3xl font-bold text-green-400 mb-2">1 MW</div>
                                    <div className="text-gray-400">Planned Capacity</div>
                                </div>

                                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                                        <i className="fas fa-server text-green-400 text-xl"></i>
                                    </div>
                                    <div className="text-3xl font-bold text-green-400 mb-2">500</div>
                                    <div className="text-gray-400">Planned Nodes</div>
                                </div>

                                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
                                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                                        <i className="fas fa-tachometer-alt text-green-400 text-xl"></i>
                                    </div>
                                    <div className="text-3xl font-bold text-green-400 mb-2">99.9%</div>
                                    <div className="text-gray-400">Target Uptime</div>
                                </div>
                            </div>

                            {/* Rest of the component remains the same... */}
                            {/* Technology Features */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
                                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                                        <i className="fas fa-microchip text-green-400 text-2xl"></i>
                                    </div>
                                    <h3 className="text-xl font-bold mb-4 text-white">Edge Computing</h3>
                                    <p className="text-gray-300 mb-4">Distributed processing nodes ensure fast and efficient computing power delivery.</p>
                                    <ul className="space-y-2 text-gray-400">
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            Low latency access
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            Fault tolerance
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            Auto-scaling
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
                                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                                        <i className="fas fa-solar-panel text-green-400 text-2xl"></i>
                                    </div>
                                    <h3 className="text-xl font-bold mb-4 text-white">Clean Energy</h3>
                                    <p className="text-gray-300 mb-4">100% renewable energy powered infrastructure with smart grid integration.</p>
                                    <ul className="space-y-2 text-gray-400">
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            Solar arrays
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            Wind power
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            Battery storage
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700">
                                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6 mx-auto">
                                        <i className="fas fa-shield-alt text-green-400 text-2xl"></i>
                                    </div>
                                    <h3 className="text-xl font-bold mb-4 text-white">Security</h3>
                                    <p className="text-gray-300 mb-4">Enterprise-grade security measures protect all data and operations.</p>
                                    <ul className="space-y-2 text-gray-400">
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            End-to-end encryption
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            Regular audits
                                        </li>
                                        <li className="flex items-center">
                                            <i className="fas fa-check text-green-400 mr-2"></i>
                                            24/7 monitoring
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <button 
                                onClick={onLearnMoreClick}
                                className="bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-700 transition-colors duration-300 transform hover:scale-105"
                            >
                                Explore Our Technology
                                <i className="fas fa-arrow-right ml-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('Technology error:', error);
        reportError(error);
        return null;
    }
}

window.Technology = Technology;

export default Technology;
